// ---------------------------------------------------------------------------
// useBindingRows — owns the per-variable runtime binding state shared by every
// eval surface. Each declared variable gets an editable `$expr` binding,
// pre-filled by convention (`getDefault`, default `defaultBindingFor`) and
// overridable by the user. `initialOverrides` seeds saved bindings when editing
// an existing template.
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useState } from 'react';

import type { BindingRow } from '../bindings.utils.js';
import { defaultBindingFor } from '../bindings.utils.js';
export interface BindingVariable {
  name: string;
  defaultValue?: unknown;
  declaredBy?: string[];
  type?: string;
  templateVariableId?: string;
}

export interface UseBindingRowsOptions {
  /** Convention default for a variable's binding. Defaults to `defaultBindingFor`. */
  getDefault?: (name: string) => string;
  /** Saved variable→expr overrides to seed (e.g. when editing a template). */
  initialOverrides?: Record<string, string>;
}

export interface UseBindingRowsResult {
  /** One effective binding per named variable (override, else convention default). */
  bindingRows: BindingRow[];
  /** Set (override) a variable's binding expression. */
  setBinding: (name: string, expr: string) => void;
}

export function useBindingRows(
  variables: BindingVariable[],
  options: UseBindingRowsOptions = {},
): UseBindingRowsResult {
  const { getDefault = defaultBindingFor, initialOverrides } = options;

  // User overrides keyed by variable name. Absent entries fall back to the
  // convention default; an explicit '' means "no binding".
  const [overrides, setOverrides] = useState<Record<string, string>>(
    () => initialOverrides ?? {},
  );

  const bindingRows = useMemo(
    () =>
      variables
        .filter(
          (variable) =>
            variable.name.trim().length > 0 ||
            variable.templateVariableId !== undefined,
        )
        .map((variable) => {
          const name = variable.name.trim();
          return {
            name,
            expr:
              name.length === 0 ? '' : (overrides[name] ?? getDefault(name)),
            ...(variable.defaultValue !== undefined
              ? { defaultValue: variable.defaultValue }
              : {}),
            ...(variable.declaredBy !== undefined
              ? { declaredBy: variable.declaredBy }
              : {}),
            ...(variable.type !== undefined ? { type: variable.type } : {}),
            ...(variable.templateVariableId !== undefined
              ? { templateVariableId: variable.templateVariableId }
              : {}),
          };
        }),
    [variables, overrides, getDefault],
  );

  const setBinding = useCallback((name: string, expr: string) => {
    setOverrides((prev) => ({ ...prev, [name]: expr }));
  }, []);

  return { bindingRows, setBinding };
}
